import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { ArrowLeft, Check, Copy, Loader, Warehouse } from 'lucide-react';
import { velocityApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/Usepermissions';
import { hasVelocityAdminRole, velocityErrorMessage } from '../utils/velocity';

const EMPTY = {
  name: '',
  phone_number: '',
  email: '',
  contact_person: '',
  gst_no: '',
  street_address: '',
  zip: '',
  city: '',
  state: '',
  country: 'India',
};

/** Ek labelled input. Component ke bahar hai taaki type karte waqt focus na tute. */
const Field = ({ id, label, required, labelCls, inputCls, ...rest }) => (
  <div>
    <label className={labelCls} htmlFor={id}>
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input id={id} className={inputCls} {...rest} />
  </div>
);

/**
 * Velocity pickup warehouse banane ka form (ADMIN only).
 * Ye ek baar ka setup hai — jo warehouseId milta hai use backend ke env me
 * daalna padta hai, frontend kahin store nahi karta.
 */
const SettingsVelocityWarehouse = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { isAdmin } = usePermissions();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const isDark = currentTheme.textColor === '#FFFFFF' || currentTheme.color === '#0f172a';
  const allowed = isAdmin || hasVelocityAdminRole();

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { message.warning('Warehouse name is required'); return; }
    if (!/^\d{10}$/.test(form.phone_number.trim())) { message.warning('Enter a 10-digit contact phone number'); return; }
    if (!form.contact_person.trim()) { message.warning("Contact person's name is required"); return; }
    if (!form.street_address.trim()) { message.warning('Street address is required'); return; }
    if (!/^\d{6}$/.test(form.zip.trim())) { message.warning('Enter a 6-digit pincode'); return; }
    if (!form.city.trim()) { message.warning('City is required'); return; }
    if (!form.state.trim()) { message.warning('State is required'); return; }

    setLoading(true);
    try {
      const res = await velocityApi.createWarehouse({
        name: form.name.trim(),
        phone_number: form.phone_number.trim(),
        email: form.email.trim(),
        contact_person: form.contact_person.trim(),
        gst_no: form.gst_no.trim(),
        address_attributes: {
          street_address: form.street_address.trim(),
          zip: form.zip.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          country: form.country.trim() || 'India',
        },
      });
      setResult(res);
      message.success(res?.message || 'Warehouse created');
    } catch (err) {
      message.error(velocityErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const copyEnv = async () => {
    const env = [
      `VELOCITY_WAREHOUSE_ID=${result?.warehouseId || ''}`,
      `VELOCITY_PICKUP_LOCATION=${form.name.trim()}`,
      `VELOCITY_PICKUP_PINCODE=${form.zip.trim()}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(env);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      message.warning('Could not copy — select the text manually');
    }
  };

  const labelCls = `block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const inputCls = `w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 ${
    isDark ? 'bg-[#1e293b] border-white/10 text-white focus:ring-white/20' : 'bg-white border-gray-200 text-gray-800 focus:ring-gray-200'
  }`;
  const cardCls = `rounded-xl border p-6 ${isDark ? 'bg-[#111827] border-white/5' : 'bg-white border-gray-100 shadow-sm'}`;

  if (!allowed) {
    return (
      <div className="min-h-screen p-8">
        <div className={`${cardCls} max-w-xl mx-auto text-center`}>
          <h1 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Admin only</h1>
          <p className="mt-2 text-sm text-gray-500">Creating a Velocity warehouse needs the ADMIN role.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a]' : ''}`}
      style={{ background: isDark ? undefined : `linear-gradient(135deg, ${currentTheme.color}10 0%, ${currentTheme.color}05 50%, #ffffff 100%)` }}
    >
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/settings/courier-api')}
          className={`mb-4 flex items-center gap-2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
        >
          <ArrowLeft className="h-5 w-5" /> Back to Courier API
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-lg shadow-md" style={{ backgroundColor: currentTheme.color }}>
            <Warehouse className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Velocity Pickup Warehouse</h1>
            <p className="text-sm text-gray-500">One-time setup. The courier collects parcels from this address.</p>
          </div>
        </div>

        {result ? (
          <div className={cardCls}>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
              <Check className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-bold text-green-800">{result.message || 'Warehouse created'}</p>
                <p className="mt-1 text-sm">
                  Warehouse ID <span className="font-mono font-bold">{result.warehouseId}</span>
                </p>
              </div>
            </div>

            <p className={`mt-5 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Add these to the backend environment file, then restart the CRM service:
            </p>
            <pre className={`mt-2 p-4 rounded-lg text-xs overflow-x-auto ${isDark ? 'bg-black/40 text-gray-200' : 'bg-gray-900 text-gray-100'}`}>
{`VELOCITY_WAREHOUSE_ID=${result.warehouseId}
VELOCITY_PICKUP_LOCATION=${form.name.trim()}
VELOCITY_PICKUP_PINCODE=${form.zip.trim()}`}
            </pre>
            <p className="mt-2 text-xs text-gray-500">
              The frontend does not store this id. Until the backend has it, shipping will fail validation.
            </p>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={copyEnv}
                className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2"
                style={{ backgroundColor: currentTheme.color }}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy settings'}
              </button>
              <button
                type="button"
                onClick={() => { setResult(null); setForm(EMPTY); }}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold border ${isDark ? 'border-white/10 text-gray-300' : 'border-gray-200 text-gray-600'}`}
              >
                Create another
              </button>
            </div>
          </div>
        ) : (
          <div className={cardCls}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-name" label="Warehouse name" required value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Yashvi Enterprise - WH" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-contact" label="Contact person" required value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} placeholder="e.g. Dev" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-phone" label="Contact phone" required value={form.phone_number} onChange={(e) => set('phone_number', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit number" inputMode="numeric" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-email" label="Email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="dispatch@example.com" />
              <div className="md:col-span-2">
                <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-street" label="Street address" required value={form.street_address} onChange={(e) => set('street_address', e.target.value)} placeholder="Building, street, area" />
              </div>
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-city" label="City" required value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="e.g. Palwal" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-state" label="State" required value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="e.g. Haryana" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-zip" label="Pincode" required value={form.zip} onChange={(e) => set('zip', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit pincode" inputMode="numeric" />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-country" label="Country" value={form.country} onChange={(e) => set('country', e.target.value)} />
              <Field labelCls={labelCls} inputCls={inputCls} disabled={loading} id="vw-gst" label="GST number (optional)" value={form.gst_no} onChange={(e) => set('gst_no', e.target.value)} />
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/settings/courier-api')}
                disabled={loading}
                className={`px-6 py-2.5 rounded-lg text-sm font-semibold border ${isDark ? 'border-white/10 text-gray-300' : 'border-gray-200 text-gray-600'}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-2.5 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: currentTheme.color }}
              >
                {loading && <Loader className="w-4 h-4 animate-spin" />}
                {loading ? 'Creating…' : 'Create warehouse'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsVelocityWarehouse;
